/**
 * Version Restore Dialog Component
 *
 * Confirmation dialog for restoring a previous version.
 * Explains the restore process and potential impacts.
 *
 * Features:
 * - Clear explanation of restore action
 * - Version details display
 * - Warning about current version changes
 * - Confirmation with loading state
 */

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/dialog'
import { Button } from '@shared/components/ui/button'
import { Badge } from '@shared/components/ui/badge'
import { Alert, AlertDescription } from '@shared/components/ui/alert'
import { AlertTriangle, RotateCcw, Clock, User } from 'lucide-react'
import { formatDateOnly } from '@shared/utils/date'
import type { Version } from '@shared/types/versioned-entity.types'

export interface VersionRestoreDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  version: Version | null
  isLoading?: boolean
}

export function VersionRestoreDialog({
  isOpen,
  onClose,
  onConfirm,
  version,
  isLoading = false,
}: VersionRestoreDialogProps) {
  if (!isOpen || !version) return null

  const handleConfirm = async () => {
    await onConfirm()
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md" overlayClassName="bg-foreground opacity-80">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Restore Version
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning alert */}
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Restoring will create a new version with the configuration from version{' '}
              {version.version.toFixed(1)}. Your current active version will be archived.
            </AlertDescription>
          </Alert>

          {/* Version details */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-semibold">Version {version.version.toFixed(1)}</span>
              <Badge variant={version.status === 'active' ? 'default' : 'secondary'}>
                {version.status}
              </Badge>
            </div>

            {version.note && (
              <p className="text-sm text-muted-foreground">{version.note}</p>
            )}

            <div className="space-y-1.5 text-xs text-muted-foreground pt-2 border-t">
              <div className="flex items-center gap-1.5">
                <User className="h-3 w-3" />
                <span>{version.createdByName || version.createdBy}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                <span>
                  {formatDateOnly(version.createdAt)}
                </span>
              </div>
              {version.effectiveDate && (
                <div className="flex items-center gap-1.5">
                  <span className="font-medium">Effective:</span>
                  <span>
                    {formatDateOnly(version.effectiveDate)}
                  </span>
                </div>
              )}
            </div>

            {/* Changes summary */}
            {version.changes && version.changes.length > 0 && (
              <div className="pt-2 border-t">
                <div className="text-xs font-medium mb-2">
                  This version includes {version.changes.length} change
                  {version.changes.length !== 1 && 's'}:
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {version.changes.slice(0, 5).map((change, idx) => (
                    <div
                      key={idx}
                      className="text-xs text-muted-foreground flex items-start gap-1.5"
                    >
                      <span className="shrink-0">•</span>
                      <span className="flex-1">
                        {change.fieldLabel || change.field}
                      </span>
                    </div>
                  ))}
                  {version.changes.length > 5 && (
                    <div className="text-xs text-muted-foreground italic">
                      and {version.changes.length - 5} more...
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Information */}
          <div className="text-sm text-muted-foreground space-y-2">
            <p>After restoring:</p>
            <ul className="space-y-1 ml-4">
              <li className="flex items-start gap-2">
                <span className="shrink-0">•</span>
                <span>
                  A new version will be created with this configuration
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0">•</span>
                <span>
                  The new version will become the active version
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="shrink-0">•</span>
                <span>
                  You can review and edit the restored version before activating
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="rounded-full h-12 font-medium bg-background border-foreground text-foreground px-6 hover:bg-muted"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading}
            className="rounded-full h-12 font-medium bg-foreground hover:bg-foreground/90 text-background px-6 shadow-lg"
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                Restoring...
              </>
            ) : (
              <>
                <RotateCcw className="mr-2 h-4 w-4" />
                Restore Version
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
